import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { breakpointQueries, useMediaQuery } from '@la/shared-components';
import {
  Player,
  PlayerInvite,
  Registrant,
  RegistrationStatus,
  Staff,
  StaffInvite,
  StaffRole,
} from '@la/types';
import {
  isExistingStaff,
  mapStaffRolePermissions,
  mergeStaffRolePermissions,
} from '@la/utilities';
import ColumnGrid from 'components/ColumnGrid/ColumnGrid';
import { getLAHostnameParts } from 'lib/utils/urlUtils';
import { getSiteIdentityData } from 'redux/coreSlice';
import { useGetRegistrationSettingsQuery } from 'redux/services/registrationApi';
import {
  useGetInvitesQuery,
  useGetRosterPermissionsQuery,
  useGetRosterQuery,
} from 'redux/services/rosterManagementApi';
import { useGetStaffRolesQuery } from 'redux/services/staffRoleApi';
import { useGetUserRegistrationsQuery } from 'redux/services/tournamentApi';
import { useAppSelector } from 'redux/store';
import { getUserId } from 'redux/userSlice';
import PageTitle from 'domains/Shell/PageTitle/PageTitle';
import { AddPlayerModal } from './AddPlayerModal/AddPlayerModal';
import CSVDownloadModal from './CSVDownloadModal';
import InvitePlayerStaffModals from './InvitePlayerStaffModals';
import { RosterActionsModal } from './RosterActionsModal/RosterActionsModal';
import { InviteModal } from './RosterRollover/InviteModal/InviteModal';
import RosterSection from './RosterSection';
import RosterSideNav from './RosterSideNav';
import RosterUtilities from './RosterUtilities';
import { getFormDataBodyFromValues } from './utilities/csv';
import {
  mapPlayersToRegistrants,
  mapStaffToRegistrants,
} from './utilities/registrant';
import * as S from './RosterManagement.styles';

export type StaffInvitation = {
  link: string;
  role: string;
  id?: string;
};

export type InvitationLinks = {
  playerInviteLink: string;
  staffInviteLinks: StaffInvitation[];
};

export type RolloverData = {
  totalPlayers: number;
  totalStaff: number;
  invites: InvitationLinks;
};

/* Roster Management Page */
export default function RosterManagement() {
  const { playerAndStaffMaxCapacity } = useFlags();
  const navigate = useNavigate();

  const { siteId, siteName } = useAppSelector(getSiteIdentityData);
  const userId = useAppSelector(getUserId);
  const { programId, programType, teamId } = useParams();
  const [searchParams] = useSearchParams();

  const rolloverEncodedData = searchParams.get('rolloverData');
  const siteSubdomain = getLAHostnameParts().subdomain;

  const [openAddModal, setOpenAddModal] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState<string | null>(null);
  const [rolloverData, setRolloverData] = useState<RolloverData | null>(null);
  const [isRolloverModalOpen, setIsRolloverModalOpen] = useState(false);
  const isTabletPortraitUp = useMediaQuery(breakpointQueries.tabletPortraitUp);

  const {
    data,
    isLoading: teamDataIsLoading,
    error: teamDataError /*isError, isLoading */,
  } = useGetRosterQuery(
    {
      siteSubdomain,
      siteId: siteId ?? '',
      programId: programId ?? '',
      teamId: teamId ?? '',
    },
    { skip: !siteId || !teamId || !programId }
  );

  const {
    data: rawStaffRolePermissions,
    isLoading: staffRolePermissionsIsLoading,
    error: staffRolePermissionsError,
  } = useGetRosterPermissionsQuery(
    {
      programId: programId ?? '',
      siteSubdomain,
      teamId: teamId ?? '',
    },
    {
      skip: !programId || !teamId,
    }
  );

  const { data: staffRoles } = useGetStaffRolesQuery({
    siteId: siteId?.toString() ?? '',
  });
  const { data: rawInvitesData, isLoading: invitesDataIsLoading } =
    useGetInvitesQuery(
      {
        programId,
        siteSubdomain,
        teamId,
      },
      { skip: !programId }
    );

  const {
    data: registrationsData,
    isLoading: registrationsDataIsLoading,
    isUninitialized: isUserRegistrationQueryUninitialized,
    error: registrationsDataError,
    refetch: userRegistrationQueryRefetch,
  } = useGetUserRegistrationsQuery(
    {
      programId: programId || '',
      siteDomain: siteSubdomain,
    },
    {
      skip: !programId && !openAddModal,
    }
  );

  const { data: registrationSettings } = useGetRegistrationSettingsQuery(
    {
      programId: programId ?? '',
      subdomain: siteSubdomain,
    },
    {
      skip: !programId,
    }
  );

  const minimumNumberOfPlayers = useMemo(() => {
    return registrationSettings?.settings.find(
      ({ settingName }) => settingName === 'minimum_number_of_team_players'
    );
  }, [registrationSettings]);

  const maximumNumberOfPlayers = useMemo(() => {
    return registrationSettings?.settings.find(
      ({ settingName }) => settingName === 'maximum_number_of_team_players'
    );
  }, [registrationSettings]);

  const minimumNumberOfStaff = useMemo(() => {
    return registrationSettings?.settings.find(
      ({ settingName }) => settingName === 'minimum_number_of_team_staff'
    );
  }, [registrationSettings]);

  const maximumNumberOfStaff = useMemo(() => {
    return registrationSettings?.settings.find(
      ({ settingName }) => settingName === 'maximum_number_of_team_staff'
    );
  }, [registrationSettings]);

  const permittedRoles = useMemo(() => {
    return registrationSettings?.settings.find((setting) => {
      return setting.settingName === 'team_staff_member_permitted_role_types';
    })?.value;
  }, [registrationSettings]);

  const invitesData: InvitationLinks = {
    playerInviteLink: rawInvitesData?.playerInviteLink ?? '',
    staffInviteLinks:
      (permittedRoles
        ? rawInvitesData?.staffInviteLinks.filter((link) => {
            const isPermitted = permittedRoles.includes(Number(link.id));

            const staffRole = staffRoles?.find(
              (role) => role.id === Number(link.id)
            );
            const hasInvitePermission = staffRole?.allowInviteThisRole ?? false;

            return isPermitted && hasInvitePermission;
          })
        : rawInvitesData?.staffInviteLinks.filter((link) => {
            // When no permitted roles restriction, still check allowInviteThisRole
            const staffRole = staffRoles?.find(
              (role) => role.id === Number(link.id)
            );
            return staffRole?.allowInviteThisRole ?? false;
          })) ?? [],
  };

  useEffect(() => {
    if (!openAddModal && !isUserRegistrationQueryUninitialized) {
      userRegistrationQueryRefetch();
    }
  }, [
    openAddModal,
    userRegistrationQueryRefetch,
    isUserRegistrationQueryUninitialized,
  ]);

  /**
   * Serializes the encoded data into local state, cleans the URL and opens the invite modal for
   * rollovered members.
   */
  useEffect(() => {
    if (rolloverEncodedData) {
      const decodedData: RolloverData = JSON.parse(
        window.atob(rolloverEncodedData)
      );
      setRolloverData(decodedData);
      setIsRolloverModalOpen(true);
      window.history.pushState({}, document.title, window.location.pathname);
    }
  }, [rolloverEncodedData, searchParams]);

  const { phoneOnly, tabletPortraitUp, tabletLandscapeUp } = breakpointQueries;
  const isPhone = useMediaQuery(phoneOnly);
  const isTabletPortrait = useMediaQuery(tabletPortraitUp);
  const isTabletLandscape = useMediaQuery(tabletLandscapeUp);

  let teamNameSize: 'large' /* | 'large-mobile' */ = 'large',
    tableTitleSize: 'medium' /* | 'medium-mobile' */ = 'medium';

  if (isPhone) {
    teamNameSize = 'large'; /* 'large-mobile' once this size is added */
    tableTitleSize = 'medium' /* 'medium-mobile' once this size is added */;
  } else if (isTabletPortrait) {
    teamNameSize = 'large';
  } else if (isTabletLandscape) {
    teamNameSize = 'large'; /* 'large-mobile' once this size is added */
  }

  const formDataBody = getFormDataBodyFromValues(teamId!, 'ExportToCSV');

  let playerRoster: Registrant[] = [];
  let staffRoster: Registrant[] = [];
  if (data) {
    const players: (Player | PlayerInvite)[] = data.players ?? [];
    const playerInvites = data.playerInvites ?? [];
    playerRoster = mapPlayersToRegistrants(players.concat(playerInvites));
    const staff: (Staff | StaffInvite)[] = data.staff ?? [];
    const staffInvites = data.staffInvites ?? [];
    staffRoster = mapStaffToRegistrants(
      staff.concat(staffInvites),
      staffRoles ?? []
    );
  }

  /**
   * 16 June 2025
   * The permission query gets the permissions successfully if the user has registered
   * through NG flows. However there are manual staff adds done via OG, and therefore we need
   * to merge the permissions because there will be no NG registration associated with it.
   * - Victoria Phillips vphillips@leagueapps.com
   */
  const userStaffRoles = staffRoster
    .filter(
      (member) =>
        member.userId === userId &&
        member.registrationStatus === RegistrationStatus.Registered
    )
    .map((member) => staffRoles?.find((role) => role.id === member.roleId))
    .filter((role): role is StaffRole => !!role);
  const userStaffRolesPermissions = mapStaffRolePermissions(userStaffRoles);

  const staffRolePermissions = useMemo(() => {
    if (rawStaffRolePermissions) {
      return mergeStaffRolePermissions(
        rawStaffRolePermissions,
        userStaffRolesPermissions
      );
    }
  }, [rawStaffRolePermissions, userStaffRolesPermissions]);

  useEffect(() => {
    document.title = siteName
      ? `${siteName}: Roster Management`
      : 'Roster Management';
  }, [siteName]);

  function handlePrinterRosterButtonClick(): void {
    window.open(
      `/printable?format=HTML&type=TEAM_ROSTER&issuedByRole=Captain&teamId=${teamId}&programId=${programId}`
    );
  }

  function handleRolloverRosterClick(): void {
    navigate('rollover');
  }

  function getCompletedRegistrationsCount(registrants: Registrant[]): number {
    return registrants.filter(
      (roster) => roster.registrationStatus === RegistrationStatus.Registered
    ).length;
  }

  const completedPlayerCount = getCompletedRegistrationsCount(playerRoster);
  let playerSpotsRemaining =
    playerAndStaffMaxCapacity && maximumNumberOfPlayers?.value
      ? maximumNumberOfPlayers.value - completedPlayerCount
      : undefined;
  if (playerSpotsRemaining !== undefined && playerSpotsRemaining < 0) {
    playerSpotsRemaining = 0;
  }

  const completedStaffCount = getCompletedRegistrationsCount(staffRoster);
  let staffSpotsRemaining =
    playerAndStaffMaxCapacity && maximumNumberOfStaff?.value
      ? maximumNumberOfStaff.value - completedStaffCount
      : undefined;
  if (staffSpotsRemaining !== undefined && staffSpotsRemaining < 0) {
    staffSpotsRemaining = 0;
  }
  const isRegisteredAsStaff =
    userId && programId && teamId && registrationsData?.userRegistrations
      ? isExistingStaff(
          registrationsData?.userRegistrations ?? [],
          parseInt(programId),
          userId,
          parseInt(teamId)
        )
      : false;

  const shouldRenderAddPlayerStaffModal: boolean =
    !teamDataIsLoading &&
    !teamDataError &&
    !staffRolePermissionsIsLoading &&
    !staffRolePermissionsError &&
    !registrationsDataIsLoading &&
    !registrationsDataError;

  const allowInvitePlayers = !!staffRolePermissions?.['allowInvitePlayers'];
  const allowInviteStaff = !!staffRolePermissions?.['allowInviteStaff'];
  const disableInvitePlayerAction =
    playerAndStaffMaxCapacity && maximumNumberOfPlayers?.value
      ? !playerSpotsRemaining
      : false;
  const disableInviteStaffAction =
    playerAndStaffMaxCapacity && maximumNumberOfStaff?.value
      ? !isRegisteredAsStaff && !staffSpotsRemaining
      : false;

  return (
    <S.RosterManagementPage>
      <PageTitle>Manage your team roster</PageTitle>
      <ColumnGrid>
        <S.MinorColumn>
          <RosterSideNav
            programId={programId!}
            programType={programType!}
            teamId={teamId!}
            teamNameSize={teamNameSize}
          />
        </S.MinorColumn>
        <S.MajorColumn>
          {isTabletPortraitUp ? (
            <RosterUtilities
              hideRosterRollover={!allowInvitePlayers || !allowInviteStaff}
              onDownloadRosterCSVClick={() => setOpenModal('csvDownload')}
              onPrintRosterClick={handlePrinterRosterButtonClick}
              onRosterRolloverClick={handleRolloverRosterClick}
            />
          ) : (
            <RosterActionsModal
              disableInvitePlayerAction={disableInvitePlayerAction}
              disableInviteStaffAction={disableInviteStaffAction}
              hideInvitePlayer={!allowInvitePlayers}
              hideInviteStaff={!allowInviteStaff}
              onAddPlayerClick={() => setOpenAddModal('addPlayers')}
              onDownloadRosterCSVClick={() => setOpenModal('csvDownload')}
              onInvitePlayerClick={() => setOpenModal('invitePlayers')}
              onInviteStaffClick={() => setOpenModal('inviteStaff')}
              onPrintRosterClick={handlePrinterRosterButtonClick}
              onRosterRolloverClick={handleRolloverRosterClick}
            />
          )}
          {data?.players ? (
            <RosterSection
              allowedToInvite={allowInvitePlayers}
              completedRegistrationsCount={completedPlayerCount}
              minimumCapacity={minimumNumberOfPlayers?.value}
              maximumCapacity={
                playerAndStaffMaxCapacity
                  ? maximumNumberOfPlayers?.value
                  : undefined
              }
              openAddModal={() => setOpenAddModal('addPlayers')}
              openModal={() => setOpenModal('invitePlayers')}
              registrations={playerRoster}
              programId={programId!}
              rosterData={playerRoster}
              rosterTitle="Players"
              rosterType="players"
              spotsRemaining={
                playerAndStaffMaxCapacity ? playerSpotsRemaining : undefined
              }
              tableTitleSize={tableTitleSize}
              teamId={teamId!}
              fee={data?.fees.player}
            />
          ) : null}
          {data?.staff ? (
            <RosterSection
              allowedToInvite={allowInviteStaff}
              completedRegistrationsCount={completedStaffCount}
              isRegisteredAsStaff={isRegisteredAsStaff}
              minimumCapacity={minimumNumberOfStaff?.value}
              maximumCapacity={
                playerAndStaffMaxCapacity
                  ? maximumNumberOfStaff?.value
                  : undefined
              }
              openModal={() => setOpenModal('inviteStaff')}
              registrations={staffRoster}
              programId={programId!}
              rosterData={staffRoster}
              rosterTitle="Staff"
              rosterType="staff"
              tableTitleSize={tableTitleSize}
              teamId={teamId!}
              spotsRemaining={
                playerAndStaffMaxCapacity ? staffSpotsRemaining : undefined
              }
              staffInviteLinks={invitesData?.staffInviteLinks}
              userRegistrationQueryRefetch={userRegistrationQueryRefetch}
              fee={data?.fees.staff}
            />
          ) : null}
        </S.MajorColumn>
      </ColumnGrid>
      {shouldRenderAddPlayerStaffModal ? (
        <AddPlayerModal
          closeModal={() => setOpenAddModal(null)}
          openModalName={openAddModal}
          playerInviteLink={invitesData?.playerInviteLink}
        />
      ) : null}
      {!invitesDataIsLoading ? (
        <InvitePlayerStaffModals
          closeModalMethod={() => setOpenModal(null)}
          invitesData={invitesData}
          openModalName={openModal}
        />
      ) : null}
      {openModal === 'csvDownload' ? (
        <CSVDownloadModal
          body={formDataBody}
          closeModal={() => setOpenModal(null)}
          programId={programId!}
          programType={programType!}
        />
      ) : null}
      {rolloverData ? (
        <InviteModal
          invitationLinks={rolloverData.invites}
          onOpenChange={() => setIsRolloverModalOpen(false)}
          open={isRolloverModalOpen}
          totalPlayers={rolloverData.totalPlayers}
          totalStaff={rolloverData.totalStaff}
        />
      ) : null}
    </S.RosterManagementPage>
  );
}
/* */
